import { useId, useState } from 'react';
import { Info, TrendingUp } from 'lucide-react';
import { formatMoney, formatMonth, formatNumber, formatPercent } from '@blimo/shared';
import { EmptyState } from '../components/empty-state.jsx';
import { Label } from '../components/label.jsx';
import { Select } from '../components/select.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table.jsx';
import { DeltaText } from './amounts.jsx';
import { TrendChart } from './trend-chart.jsx';

const euroAxis = (v) => `${formatNumber(v / 1000, { maximumFractionDigits: 0 })} T€`;
const money = (v) => formatMoney(v, { fractionDigits: 0 });
const percent = (v) => formatPercent(v);

/**
 * Rent development & gross yield (GET …/finance/rent-development). Only displays API values:
 * totals per month, or – with `propertyId` – the values of that property (selection, no maths).
 * Months without income are omitted by the API, so the series may have gaps.
 * @param {{ data: any, propertyId?: string|number }} props
 */
export function RentDevelopmentView({ data, propertyId }) {
  const months = data?.months ?? [];
  const allProperties = data?.properties ?? [];
  const selected = propertyId ? allProperties.find((p) => String(p.id) === String(propertyId)) : null;
  const properties = selected ? [selected] : allProperties;

  if (months.length === 0) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="Keine Mieteinnahmen im Zeitraum"
        description="Für den gewählten Zeitraum liegen keine Mieteinnahmen vor. Monate ohne Einnahmen werden nicht ausgewiesen."
      />
    );
  }

  const rows = months.map((month) => {
    const entry = selected ? month.properties.find((p) => String(p.property_id) === String(selected.id)) : null;
    return {
      period: month.period,
      label: formatMonth(month.period),
      rent: selected ? (entry?.annual_rent ?? null) : month.total_annual_rent,
      yield: selected ? (entry?.yield ?? null) : month.average_yield,
      deviation: selected ? (entry?.deviation ?? null) : month.total_deviation,
    };
  });
  const scopeLabel = selected ? selected.name : properties.length > 1 ? 'alle Liegenschaften' : (properties[0]?.name ?? '');

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 lg:grid-cols-2">
        <TrendChart
          title={selected ? 'Jahresmiete' : 'Jahresmiete gesamt'}
          description={`${scopeLabel} · Monatsmiete (Z1 + Z2) × 12`}
          data={rows}
          series={[{ key: 'rent', label: 'Jahresmiete', color: 'var(--chart-1)' }]}
          formatValue={money}
          formatAxis={euroAxis}
        />
        <TrendChart
          title={selected ? 'Bruttomietrendite' : 'Ø Bruttomietrendite'}
          description={`${scopeLabel} · Jahresmiete / Kaufpreis`}
          data={rows}
          series={[{ key: 'yield', label: 'Rendite', color: 'var(--chart-1)' }]}
          formatValue={percent}
          formatAxis={(v) => formatPercent(v, { fractionDigits: 1 })}
        />
      </div>

      <MonthlyTable rows={rows} selected={Boolean(selected)} />
      <PropertyTable data={data} months={months} properties={properties} showTotals={!selected && properties.length > 1} />

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Bruttomietrendite = Jahresmiete (Grundmiete Z1 + Stellplatz Z2, hochgerechnet) / Kaufpreis. Veränderung = gegenüber dem vorherigen ausgewiesenen Monat,
        im ersten Monat gegenüber dem Startwert. Monate ohne Mieteinnahmen werden nicht ausgewiesen.
      </p>
    </div>
  );
}

function MonthlyTable({ rows, selected }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h2 id={id} className="text-sm font-semibold">
        Monatswerte
      </h2>
      <Table aria-label="Mietentwicklung je Monat" containerClassName="max-h-96 rounded-md border">
        <TableHeader>
          <TableRow>
            <TableHead>Monat</TableHead>
            <TableHead data-numeric="true">{selected ? 'Jahresmiete' : 'Jahresmiete gesamt'}</TableHead>
            <TableHead data-numeric="true">{selected ? 'Rendite' : 'Ø Rendite'}</TableHead>
            <TableHead data-numeric="true">Veränderung</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.period}>
              <TableCell className="numeric">{row.label}</TableCell>
              <TableCell data-numeric="true">{formatMoney(row.rent)}</TableCell>
              <TableCell data-numeric="true">{formatPercent(row.yield)}</TableCell>
              <TableCell data-numeric="true">
                <DeltaText value={row.deviation} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

/** Per-property values of one month (default: latest month in the range). */
function PropertyTable({ data, months, properties, showTotals }) {
  const id = useId();
  const [period, setPeriod] = useState(null);
  const month = months.find((m) => m.period === period) ?? months.at(-1);
  const byId = new Map(month.properties.map((p) => [String(p.property_id), p]));

  return (
    <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <h2 id={`${id}-heading`} className="text-sm font-semibold">
          Je Liegenschaft
        </h2>
        <div className="flex items-center gap-2">
          <Label htmlFor={`${id}-month`} className="text-xs text-muted-foreground">
            Monat
          </Label>
          <Select id={`${id}-month`} size="sm" value={month.period} onChange={(e) => setPeriod(e.target.value)} wrapperClassName="w-40">
            {months.map((m) => (
              <option key={m.period} value={m.period}>
                {formatMonth(m.period)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <Table aria-label="Mietentwicklung je Liegenschaft" containerClassName="rounded-md border">
        <TableHeader>
          <TableRow>
            <TableHead>Liegenschaft</TableHead>
            <TableHead data-numeric="true">Kaufpreis</TableHead>
            <TableHead data-numeric="true">Startwert (Jahresmiete)</TableHead>
            <TableHead data-numeric="true">Jahresmiete</TableHead>
            <TableHead data-numeric="true">Rendite</TableHead>
            <TableHead data-numeric="true">Veränderung</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {properties.map((property) => {
            const entry = byId.get(String(property.id));
            return (
              <TableRow key={property.id}>
                <TableCell className="min-w-40">
                  <span className="font-medium">{property.name}</span>
                  {property.number && <span className="numeric ml-1 text-xs text-muted-foreground">({property.number})</span>}
                </TableCell>
                <TableCell data-numeric="true">{formatMoney(property.purchase_price)}</TableCell>
                <TableCell data-numeric="true">{formatMoney(property.initial_rent)}</TableCell>
                <TableCell data-numeric="true">{formatMoney(entry?.annual_rent)}</TableCell>
                <TableCell data-numeric="true">
                  {entry?.yield == null ? <span title="Kein Kaufpreis hinterlegt">–</span> : formatPercent(entry.yield)}
                </TableCell>
                <TableCell data-numeric="true">
                  <DeltaText value={entry?.deviation} />
                </TableCell>
              </TableRow>
            );
          })}
          {showTotals && (
            <TableRow className="bg-muted/50 font-semibold hover:bg-muted/50">
              <TableCell>Gesamt</TableCell>
              <TableCell />
              <TableCell />
              <TableCell data-numeric="true">{formatMoney(month.total_annual_rent)}</TableCell>
              <TableCell data-numeric="true">{formatPercent(month.average_yield)}</TableCell>
              <TableCell data-numeric="true">
                <DeltaText value={month.total_deviation} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <p className="text-sm">
        <span className="text-muted-foreground">Ø Start-Rendite{(data?.properties?.length ?? 0) > 1 ? ' (alle Liegenschaften)' : ''}: </span>
        <span className="numeric font-semibold">{data?.start_yield_average == null ? 'nicht hinterlegt' : formatPercent(data.start_yield_average)}</span>
      </p>
    </section>
  );
}
